/* eslint-disable no-dupe-else-if */
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import {
  AppContext,
  DatePicker,
  SelectOption,
  RBAC,
  ErrorHandling,
  SelectSearch,
  Input,
  assets,
  GroupPolicyPermission,
  MenuPermissions
} from '@pulseops/common'
import { useIsFocused } from '@react-navigation/native'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  makeStyles
} from '@material-ui/core'
import { Throwable, ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/lib/function'
import * as O from 'fp-ts/Option'
import { addMonths, isAfter, startOfDay } from 'date-fns/fp'
import { Controller, useForm } from 'react-hook-form'
import moment from 'moment'
import _ from 'lodash'
import { SeaAutoDebitRegisterReportModel } from './SEAAutoDebitRegisterReportModel'
import { SEAAutoDebitRegisterReportConst } from './SEAReportAutoDebitScreenConst'
import { SEAAutoDebitRegisterReportService } from './SEAAutoDebitRegisterReportService'
import { useLoading } from '@mxt/zio-react'
import { StackScreenProps } from '@react-navigation/stack'
import { ReportsStackParamList } from '../ReportsStackParamList'

export interface ReportAutoDetailItem {
  policyNumber: string
  fromDate: string
  toDate: string
  paymentStatus: string
  currentFunction: string
  fundTopup1: string
  fundTopup2: string
  fundTopup3: string
  fundTopup4: string
  fundTopup5: string
  fundTopup6: string
  fundTopup7: string
  chargeDate: string
  amlRegister: string
  amlResult: string
  amlTime: string
  note: string
  redFlagResult: string
}

export interface ReportAutoDebitDetailFormData {
  fromDate: Date | null
  toDate: Date | null
  policyNumber: string
  status: SelectOption
}

type props = StackScreenProps<ReportsStackParamList, 'ReportAutoDebitScreen'>
export const AutoDebitRegisterReportScreen = ({ navigation }: props) => {
  let isView = false
  const { t, i18n } = useTranslation('BillingChange')
  const billingChanceImportHistoryForm = useForm<ReportAutoDebitDetailFormData>({
    defaultValues: {
      fromDate: new Date(),
      toDate: new Date(),
      status: {
        label: i18n.language === 'en' ? 'All' : 'Tất cả',
        value: SeaAutoDebitRegisterReportModel.Status.ALL
      }
    }
  })
  const [loading, bindLoading] = useLoading()
  const { control, setValue, watch } = billingChanceImportHistoryForm
  const [maxDate] = React.useState<Date>(new Date())
  const [dataSource, setDataSource] = React.useState<ReportAutoDetailItem[]>([])
  const isFocused = useIsFocused()
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [totalItem, setTotalItem] = React.useState<number>(0)
  const { changeBreadcrumb, showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const { height } = useWindowDimensions()
  const permissions: string[] = pipe(RBAC.permissions, ErrorHandling.runDidMount([]))

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            navigation.navigate('HomeScreen')
          }
        },
        {
          title: t('AutoDebitTopupAccountMonthlyRegisterReport'),
          navigate: null
        }
      ])
    }
  }, [isFocused])

  const getQuery = (input: {
    order: string | undefined
    sort: string | undefined
    start: number
    page: number
    size: number | undefined
    policyNumber: string | undefined
    status: SeaAutoDebitRegisterReportModel.Status
    fromDate: Date
    toDate: Date
  }) => {
    const { policyNumber, status, fromDate, toDate, size, start, page, order, sort } = input
    return {
      order,
      sort,
      start,
      page,
      size,
      policyNumber,
      status,
      fromDate: moment(fromDate).startOf('day').toDate(),
      toDate: moment(toDate).endOf('day').toDate()
    }
  }

  const colorStatus = (value: string) => {
    if (value === 'N') {
      return '#3cb1fd'
    } else if (value === 'D') {
      return 'red'
    } else {
      return '#ffff'
    }
  }
  const nameStatus = (value: string) => {
    if (value === 'N') {
      return i18n.language === 'en' ? 'Register' : 'Đăng ký'
    } else if (value === 'D') {
      return i18n.language === 'en' ? 'Cancel' : 'Hủy'
    } else {
      return value
    }
  }

  const mappingDataSource = (data: SeaAutoDebitRegisterReportModel.ReportDebitDataItem, start = 0) => {
    const filteredDataSource = data.map((item, index) => ({
      policyNumber: item.policyNumber || '-',
      fromDate: moment(item.fromDate).format('DD/MM/YYYY') || '-',
      toDate: moment(item.toDate).format('DD/MM/YYYY') || '-',
      currentFunction: item.currentFunction || '-',
      fundTopup1: item.fundTopup1 || '-',
      fundTopup2: item.fundTopup2 || '-',
      fundTopup3: item.fundTopup3 || '-',
      fundTopup4: item.fundTopup4 || '-',
      fundTopup5: item.fundTopup5 || '-',
      fundTopup6: item.fundTopup6 || '-',
      fundTopup7: item.fundTopup7 || '-',
      paymentStatus: item.paymentStatus || '-',
      chargeDate: moment(item.chargeDate).format('DD/MM/YYYY') || '-',
      amlRegister: item.amlRegister || '-',
      amlResult: item.amlResult || '-',
      amlTime: item.amlTime || '-',
      note:
        item.note === 'NOT_ENOUGH_AMOUNT_FOR_PREMIUM_FREQUENCY'
          ? 'GTTK đầu tư thêm không đủ để đóng phí bảo hiểm định kỳ'
          : '-',
      redFlagResult: item.redFlagResult || '-'
    }))
    if (isView) {
      setDataSource(filteredDataSource)
    }
    return filteredDataSource
  }

  const handleReportData = async (
    handle: (report: SeaAutoDebitRegisterReportModel.ReportsDebitData) => ZIO<any, Throwable, any>
  ) => {
    const size = isView ? pageSize : 0
    const customedFromDate = watch('fromDate') as Date
    const customedToDate = watch('toDate') as Date
    const selectedstatus = watch('status') || null
    const policyNumber = watch('policyNumber') || null
    const customedCate = selectedstatus?.value as SeaAutoDebitRegisterReportModel.Status
    if (await validateDataFields(customedFromDate, customedToDate)) {
      const query = getQuery({
        order: 'desc',
        sort: 'createdDate',
        start: 0,
        page: 0,
        size,
        policyNumber: policyNumber ?? '',
        status: customedCate,
        fromDate: customedFromDate,
        toDate: customedToDate
      })
      showGlobalLoading(true)
      pipe(
        SEAAutoDebitRegisterReportService.getReportData(query),
        ZIO.flatMap((report) =>
          pipe(
            O.fromNullable(report.data),
            O.filter((data) => data.length > 0),
            O.fold(
              () => {
                setDataSource([])
                showToast(t('message:MS030029'), 'error')
                showGlobalLoading(false)
                return ZIO.unit
              },
              (data) => {
                if (isView) {
                  mappingDataSource(data)
                }
                showGlobalLoading(false)
                return handle(report)
              }
            )
          )
        ),
        ZIO.catchAll(() => {
          setDataSource([])
          showToast(t('message:MS030029'), 'error')
          showGlobalLoading(false)
          return ZIO.unit
        }),
        bindLoading,
        ZIO.unsafeRun({})
      )
    }
  }

  const handleReportDataToExport = async (
    handle: (reportData: SeaAutoDebitRegisterReportModel.ReportDebitDataItem) => ZIO<any, Throwable, any>
  ) => {
    const customedFromDate = watch('fromDate') as Date
    const customedToDate = watch('toDate') as Date
    const selectedstatus = watch('status')
    const policyNumber = watch('policyNumber')
    const customedCate = selectedstatus.value as SeaAutoDebitRegisterReportModel.Status
    const isDataFieldValid = await validateDataFields(customedFromDate, customedToDate)
    if (isDataFieldValid) {
      const query = getQuery({
        order: 'desc',
        sort: 'createdDate',
        start: 0,
        page: 0,
        size: totalItem,
        policyNumber: policyNumber ?? '',
        status: customedCate,
        fromDate: customedFromDate,
        toDate: customedToDate
      })
      const totalItems = await pipe(
        SEAAutoDebitRegisterReportService.getReportData(query),
        ZIO.map((response) => {
          return response.data ? response.data?.length : 0
        }),
        ZIO.catchAll((_) => ZIO.succeed(0)),
        ZIO.unsafeRun({})
      )
      if (totalItems > 0) {
        const PAGE_SIZE = 5000
        const totalPages = Math.ceil(totalItems / PAGE_SIZE)

        await pipe(
          ZIO.sequencePar(
            _.times(totalPages, (page) =>
              pipe(
                SEAAutoDebitRegisterReportService.getReportData(query),
                ZIO.catchAll((_) => ZIO.succeed({ data: [] })),
                ZIO.map((response) => response?.data || [])
              )
            )
          ),
          ZIO.map(_.flatten),
          ZIO.flatMap((data) => {
            if (data.length === totalItems) {
              return handle(data)
            } else {
              setDataSource([])
              showToast(t('message:MS030029'), 'error')
            }
            return ZIO.unit
          }),
          ZIO.unsafeRun({})
        )
      } else {
        setDataSource([])
        showToast(t('message:MS030029'), 'error')
      }
    }
  }

  const handlePaging = (pageIndex: number, currentpageSize: number) => {
    const start = currentpageSize * pageIndex
    const customedFromDate = watch('fromDate') as Date
    const customedToDate = watch('toDate') as Date
    const selectedStatus = watch('status')
    const policyNumber = watch('policyNumber')

    const customedStatus = selectedStatus.value as SeaAutoDebitRegisterReportModel.Status
    const query = getQuery({
      order: 'desc',
      sort: 'createdDate',
      start,
      page: pageIndex,
      size: currentpageSize,
      policyNumber: policyNumber ?? '',
      status: customedStatus,
      fromDate: customedFromDate,
      toDate: customedToDate
    })
    showGlobalLoading(true)
    isView = true
    pipe(
      SEAAutoDebitRegisterReportService.getReportData(query),
      ZIO.flatMap((report) =>
        pipe(
          O.fromNullable(report.data),
          O.filter((data) => data.length > 0),
          O.fold(
            () => {
              showToast(t('message:MS030029'), 'error')
              setDataSource([])
              showGlobalLoading(false)
              return ZIO.unit
            },
            (data) => {
              mappingDataSource(data, pageIndex)
              showGlobalLoading(false)
              return ZIO.unit
            }
          )
        )
      ),
      ZIO.catchAll(() => {
        showToast(t('message:MS030029'), 'error')
        setDataSource([])
        showGlobalLoading(false)
        return ZIO.unit
      }),
      bindLoading,
      ZIO.unsafeRun({})
    )
  }

  const setDefaultValues = () => {
    const currentDate = new Date()
    if (!watch('fromDate') && !watch('toDate')) {
      setValue('fromDate', currentDate)
      setValue('toDate', currentDate)
    }
  }

  const validateDataFields = async (fromDateItem: Date, toDateItem: Date) => {
    const customedFromDate = moment(fromDateItem).format('YYYY/MM/DD')
    const customedToDate = moment(toDateItem).format('YYYY/MM/DD')
    const isValidForm = await billingChanceImportHistoryForm.trigger()
    if (!isValidForm) {
      return false
    } else if (!moment(fromDateItem).isValid()) {
      return false
    } else if (!moment(customedToDate).isValid()) {
      return false
    } else if (fromDateItem && !toDateItem) {
      return false
    } else if (!fromDateItem && toDateItem) {
      return false
    } else if (moment(customedFromDate) > moment(customedToDate)) {
      showToast(t('message:MS030044'), 'error')
      return false
    } else if (
      moment(customedFromDate).valueOf() > moment().valueOf() ||
      moment(customedToDate).valueOf() > moment().valueOf()
    ) {
      return false
    } else if (
      pipe(fromDateItem, startOfDay, addMonths(3), (fromDateAdd3Months) =>
        isAfter(fromDateAdd3Months)(startOfDay(toDateItem))
      )
    ) {
      showToast(t('message:MS080004', { k: 3 }), 'error')
      return false
    }
    return true
  }

  const onViewReportClick = () => {
    if (watch('fromDate') === null) {
      return showToast(t('message:MS020009', { field: t('common:FromDate') }), 'error')
    } else if (watch('toDate') === null) {
      return showToast(t('message:MS020009', { field: t('common:ToDate') }), 'error')
    } else if (watch('toDate') === null && watch('fromDate') === null) {
      return showToast(t('message:MS030044'), 'error')
    }
    isView = true
    setDefaultValues()
    handleReportData((report) => {
      setPageNum(0)
      setTotalItem(report.total)
      return ZIO.unit
    })
  }

  const onExcelExport = async () => {
    if (watch('fromDate') === null) {
      return showToast(t('message:MS020009', { field: t('common:FromDate') }), 'error')
    } else if (watch('toDate') === null) {
      return showToast(t('message:MS020009', { field: t('common:ToDate') }), 'error')
    } else if (watch('toDate') === null && watch('fromDate') === null) {
      return showToast(t('message:MS030044'), 'error')
    }
    showGlobalLoading(true)
    isView = false
    setDefaultValues()
    await handleReportDataToExport((reportData) => {
      const customedFromDate = watch('fromDate') as Date
      const customedToDate = watch('toDate') as Date
      const selectedstatus = watch('status')
      const namestatus = selectedstatus.label ?? '-'
      const summary = {
        fromDate: customedFromDate,
        toDate: customedToDate,
        title: t('AUTODEBITTOPUPACCOUNTMONTHLYREGISTERREPORT'),
        status: namestatus
      }
      const columns = SEAAutoDebitRegisterReportConst.displayedColumns.map(({ name, widthPx, horizontal }) => ({
        name: t(name),
        horizontal,
        width: widthPx
      }))
      const exportDataSource = mappingDataSource(reportData)
      const rows = exportDataSource.map(
        ({
          policyNumber,
          fromDate,
          toDate,
          fundTopup1,
          fundTopup2,
          fundTopup3,
          fundTopup4,
          fundTopup5,
          fundTopup6,
          fundTopup7,
          paymentStatus,
          chargeDate,
          amlResult,
          redFlagResult,
          note
        }) => [
          policyNumber,
          fromDate,
          toDate,
          fundTopup1,
          fundTopup2,
          fundTopup3,
          fundTopup4,
          fundTopup5,
          fundTopup6,
          fundTopup7,
          nameStatus(paymentStatus),
          chargeDate,
          amlResult,
          redFlagResult,
          note
        ]
      )
      isView = true
      return i18n.language === 'en'
        ? SEAAutoDebitRegisterReportService.exportTemplate({ ref: 'B8', colRef: 2, rowRef: 8 }, summary, columns, rows)
        : SEAAutoDebitRegisterReportService.exportTemplateVI(
            { ref: 'B8', colRef: 2, rowRef: 8 },
            summary,
            columns,
            rows
          )
    })
    showGlobalLoading(false)
  }
  const useStyles = makeStyles({
    container: {
      border: `1px solid #D3DCE6`,
      borderWidth: 1,
      borderRadius: 8,
      maxHeight: height - 340
    },
    table: {},
    tableHeader: {
      backgroundColor: '#E7E7E7'
    },
    tableBody: {
      backgroundColor: '#FFFFFF'
    },
    tableCellHeader: {
      backgroundColor: '#F4F4F4',
      whiteSpace: 'nowrap',
      fontWeight: 'bold',
      color: '#70777E'
    },
    tableCell: {
      whiteSpace: 'nowrap'
    }
  })

  const classes = useStyles()
  return (
    <SafeAreaView style={{ flex: 1 }}>
      <ScrollView>
        <View
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#D3DCE6',
            margin: 15,
            padding: 25
          }}
        >
          <View>
            <View style={{ flex: 1, flexDirection: 'row' }}>
              <View style={{ flex: 1, alignItems: 'flex-end' }}>
                <Controller
                  name="fromDate"
                  control={control}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <View style={[{ marginHorizontal: 15, marginBottom: 15, width: '70%' }, { marginTop: 6 }]}>
                      <DatePicker
                        keyboardIconRed={true}
                        label={t('common:FromDate')}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        errorMessage={value ? '' : error?.message}
                        maxDate={maxDate}
                        // minDate={result}
                        alwaysShow={true}
                      />
                    </View>
                  )}
                />
              </View>

              <View style={{ flex: 1, alignItems: 'flex-start' }}>
                <Controller
                  name="toDate"
                  control={control}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <View style={[{ marginHorizontal: 15, marginBottom: 15, width: '70%' }, { marginTop: 6 }]}>
                      <DatePicker
                        keyboardIconRed={true}
                        label={t('common:ToDate')}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        errorMessage={value ? '' : error?.message}
                        maxDate={maxDate}
                        alwaysShow={true}
                      />
                    </View>
                  )}
                />
              </View>
            </View>
            <View style={{ flex: 1, flexDirection: 'row' }}>
              <View style={{ flex: 1, alignItems: 'flex-end' }}>
                <Controller
                  name="policyNumber"
                  control={control}
                  render={({ field }) => (
                    <View style={{ marginHorizontal: 15, marginBottom: 15, marginTop: 20, width: '70%' }}>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        title={t('Policy')}
                        maxLength={9}
                        inputType={'number'}
                      />
                    </View>
                  )}
                />
              </View>
              <View style={{ flex: 1, alignItems: 'flex-start' }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <View style={{ marginHorizontal: 15, marginBottom: 15, marginTop: 15, width: '70%' }}>
                      <SelectSearch
                        label={t('Status')}
                        options={
                          i18n.language === 'en'
                            ? SEAAutoDebitRegisterReportConst.Status
                            : SEAAutoDebitRegisterReportConst.StatusVi
                        }
                        placeholder={t('common:Select')}
                        onChange={(val) => {
                          onChange(val)
                        }}
                        onBlur={() => {
                          onChange(
                            (!value || !value.value) && { label: i18n.language === 'en' ? 'All' : 'Tất cả', value: '' }
                          )
                        }}
                        value={value}
                        labelStyle={{ marginBottom: 8 }}
                        errorMessage={error?.message}
                        required={false}
                        popupIcon={<assets.ArrowDownDropdownIcon />}
                      />
                    </View>
                  )}
                />
              </View>
            </View>
          </View>

          <View
            style={[
              SUREStyle.sectionRow,
              SUREStyle.secondLine,
              { justifyContent: 'center', alignItems: 'center', alignSelf: 'center', padding: 20 }
            ]}
          >
            <View
              style={[
                SUREStyle.col_4,
                { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }
              ]}
            >
              {permissions && permissions.includes(GroupPolicyPermission.SearchReportAutoDebitMonthlyRegister) && (
                <TouchableOpacity style={{}} onPress={onViewReportClick}>
                  <View style={[SUREStyle.primaryBtn, { width: i18n.language === 'vi' ? 150 : 120, height: 40 }]}>
                    <assets.SearchTaskIcon />
                    <View style={{ width: 10 }} />
                    <Text style={{ textAlign: 'center', color: '#fff', fontWeight: '600' }}>{t('Search')}</Text>
                  </View>
                </TouchableOpacity>
              )}

              <View style={{ width: 25 }} />
              {permissions && permissions.includes(GroupPolicyPermission.ExportReportAutoDebitMonthlyRegister) && (
                <TouchableOpacity style={{}} onPress={onExcelExport}>
                  <View style={[SUREStyle.primaryBtn, { width: i18n.language === 'vi' ? 150 : 120, height: 40 }]}>
                    <View
                      style={{
                        width: 20,
                        height: 22,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignSelf: 'center'
                      }}
                    >
                      <assets.ExportCurve />
                    </View>
                    <View style={{ width: 10 }} />
                    <Text style={{ textAlign: 'center', color: '#fff', fontWeight: '600' }}>{t('Export')}</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
        {dataSource && dataSource.length > 0 ? (
          <View style={{ padding: 20 }}>
            <TableContainer component={Paper} style={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell className={classes.tableCellHeader} style={{ width: 100 }}>
                      {t('Policy')}
                    </TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('RegisterAutoDebitFromDate')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('RegisterAutoDebitToDate')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup1')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup2')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup3')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup4')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup5')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup6')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('PriorityFundTopup7')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('RegisterStatus')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('RegisterDate')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('AMLStatus')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('RedFlagStatus')}</TableCell>
                    <TableCell className={classes.tableCellHeader}>{t('Note')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataSource.map((item, index) => {
                    return (
                      <TableRow key={1}>
                        <TableCell className={classes.tableCell}>
                          <Text
                            style={{
                              fontWeight: '600',
                              fontSize: 15,
                              color: '#1ea5fc',
                              textDecorationLine: 'none'
                            }}
                          >
                            {item.policyNumber}
                          </Text>
                        </TableCell>

                        <TableCell className={classes.tableCell}>{item.fromDate}</TableCell>
                        <TableCell className={classes.tableCell}>{item.toDate}</TableCell>
                        <TableCell className={classes.tableCell}> {item.fundTopup1}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup2}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup3}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup4}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup5}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup6}</TableCell>
                        <TableCell className={classes.tableCell}>{item.fundTopup7}</TableCell>
                        <TableCell className={classes.tableCell} style={{ color: colorStatus(item.paymentStatus) }}>
                          {nameStatus(item.paymentStatus)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>{item.chargeDate}</TableCell>
                        <TableCell className={classes.tableCell}>{item.amlResult}</TableCell>
                        <TableCell className={classes.tableCell}>{item.redFlagResult}</TableCell>
                        <TableCell className={classes.tableCell}>{item.note}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              page={pageNum}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              rowsPerPage={pageSize}
              count={totalItem}
              onPageChange={(e, page) => {
                setPageNum(page)
                handlePaging(page, pageSize)
              }}
              onRowsPerPageChange={(e) => {
                const rowsPerPage = Number(e.target.value)
                setPageSize(rowsPerPage)
                if (pageNum * rowsPerPage >= totalItem) {
                  setPageNum(0)
                  handlePaging(0, rowsPerPage)
                } else {
                  handlePaging(pageNum, rowsPerPage)
                }
              }}
              labelRowsPerPage={t('common:PaginationSize')}
              labelDisplayedRows={({ from, to, count }) => t('common:Pagination', { from, to, count })}
              component={View}
            ></TablePagination>
          </View>
        ) : (
          !loading && (
            <View style={{ marginHorizontal: 30 }}>
              <Text style={{ color: '#ED1B2E' }}>{t('message:MS030029')}</Text>
            </View>
          )
        )}
      </ScrollView>
    </SafeAreaView>
  )
}

const SUREStyle = StyleSheet.create({
  pageContent: {
    backgroundColor: '#fff',
    height: '100%',
    width: '100%'
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    paddingLeft: 15,
    paddingRight: 15
  },
  sectionContent: {
    padding: 20,
    backgroundColor: '#fafafa',
    border: '1px solid #d3dce6',
    boxSizing: 'border-box',
    borderRadius: 8,
    marginTop: 30
  },
  sectionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    marginRight: -15,
    marginLeft: -15
  },
  secondLine: {
    marginTop: 20
  },
  col_4: {
    width: '100%',
    maxWidth: '33.3333333333%',
    paddingRight: 15,
    paddingLeft: 15
  },
  primaryBtn: {
    flexDirection: 'row',
    backgroundColor: '#ed1b2e',
    borderRadius: 100,
    padding: 10,
    boxShadow: 'none',
    justifyContent: 'center'
  },
  cellWidth200: {
    minWidth: '200px'
  }
})
